import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from "react";
import styles from "./Field.module.css";

interface FieldShellProps {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  children: ReactNode;
}

/** Label + hint + error scaffold reused by every input primitive. */
export function FieldShell({ label, htmlFor, hint, error, required, children }: FieldShellProps) {
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={htmlFor}>
        {label}
        {required && <span className={styles.req} aria-hidden="true">*</span>}
      </label>
      {children}
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className={styles.hint}>{hint}</p>
      ) : null}
    </div>
  );
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  mono?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, mono, required, className, ...rest },
  ref,
) {
  const id = useId();
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined;
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && <span className={styles.req} aria-hidden="true">*</span>}
      </label>
      <input
        ref={ref}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={[styles.input, mono ? styles.mono : "", error ? styles.invalid : "", className ?? ""]
          .filter(Boolean)
          .join(" ")}
        {...rest}
      />
      {error ? (
        <p id={`${id}-err`} className={styles.error} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className={styles.hint}>
          {hint}
        </p>
      ) : null}
    </div>
  );
});

function EyeIcon() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M2 10s2.9-5 8-5 8 5 8 5-2.9 5-8 5-8-5-8-5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="10" cy="10" r="2.25" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M2 10s2.9-5 8-5c1.2 0 2.3.28 3.3.72M18 10s-2.9 5-8 5c-1.2 0-2.3-.28-3.3-.72" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.2 8.2a2.25 2.25 0 003.18 3.18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M3.5 3.5l13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * A masked text input with a show/hide (eye) toggle — the common password-field
 * affordance (COG-17). Forwards the ref to the underlying <input> so it drops
 * straight into react-hook-form `register(...)`. The type flips password↔text on
 * toggle; the value is never echoed anywhere else.
 */
export const PasswordField = forwardRef<HTMLInputElement, Omit<TextFieldProps, "type">>(
  function PasswordField({ label, hint, error, mono, required, className, ...rest }, ref) {
    const [reveal, setReveal] = useState(false);
    const id = useId();
    const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined;
    return (
      <div className={styles.field}>
        <label className={styles.label} htmlFor={id}>
          {label}
          {required && <span className={styles.req} aria-hidden="true">*</span>}
        </label>
        <div className={styles.pwWrap}>
          <input
            ref={ref}
            id={id}
            type={reveal ? "text" : "password"}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={[styles.input, styles.pwInput, mono ? styles.mono : "", error ? styles.invalid : "", className ?? ""]
              .filter(Boolean)
              .join(" ")}
            {...rest}
          />
          <button
            type="button"
            className={styles.pwToggle}
            onClick={() => setReveal((v) => !v)}
            aria-label={reveal ? "Hide secret" : "Show secret"}
            aria-pressed={reveal}
            title={reveal ? "Hide" : "Show"}
            tabIndex={-1}
          >
            {reveal ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
        {error ? (
          <p id={`${id}-err`} className={styles.error} role="alert">
            {error}
          </p>
        ) : hint ? (
          <p id={`${id}-hint`} className={styles.hint}>
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
